import { html, LitElement, nothing, TemplateResult } from "lit";
import {
  customElement,
  property,
  queryAssignedNodes,
  query,
  state,
} from "lit/decorators.js";
import { IconDefaultType as IconType, SlhIcon } from "@slhuiicon/icon";
import { Translatable } from "@slhui/i18n/src/mixins/translatable";
import { SlhFormElement } from "@slhui/base/form";
import {
  SlhCoreFileInput,
  UploadedFile,
  ExistingFile,
} from "@slhuicore/file-input";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { SlhLoader } from "@slhui/loader";
import {
  SlhFileInputItem,
  FILE_REMOVE,
  EXISTING_FILE_REMOVE,
} from "@slhui/file-input-item";
import { SlhFormIcons } from "@slhui/form-icons";
import * as Constants from "./file-input.constants";
import { style } from "./assets/file-input.generated-style";

/**
 * File input component
 *
 * @cssprop --slh-color-neutral-shade-60 - File input icon color, hint font color and label font color.
 * @cssprop --slh-default-font-family - File input font family.
 * @cssprop --slh-base-bold-font-weight - File input font weight.
 * @cssprop --slh-base-s-font-size - File input droppable description slot font size.
 * @cssprop --slh-base-xs-font-size - File input font size and label font size.
 * @cssprop --slh-base-xxs-line-height - File input line heght, error line height and label line height.
 * @cssprop --slh-base-xxs-font-size - File input hint font size and error font size.
 * @cssprop --slh-base-semi-bold-font-weight - File input error font weight and label font weight.
 * @cssprop --slh-color-error-darken - File input error font color.
 * @cssprop --slh-color-error-shade-60 - File input error border color.
 * @cssprop --slh-color-neutral - File input hover font color.
 * @cssprop --slh-color-neutral-shade-80-opacity-18 - File input focus shadow color.
 * @cssprop --slh-color-neutral-shade-80 - File input font color.
 * @cssprop --slh-color-neutral-shade-60 - File input label, droppable description and hint font color.
 * @cssprop --slh-color-neutral-shade-40 - File input disabled font color.
 * @cssprop --slh-color-neutral-shade-20 - File input disabled background color.
 * @cssprop --slh-color-neutral-shade-10 - File input disabled drag & drop background color.
 * @cssprop --slh-color-neutral-shade-00 - File input drag & drop background color.
 * @cssprop --slh-color-outline - File input focused element on tab border color.
 * @cssprop --slh-base-bold-font-weight - File input hint font weight.
 *
 * @slot default - File input value text.
 * @slot label - File input label.
 * @slot hint - Custom content below the file input, designed to contain recommendations for user input.
 * @slot error - Custom error text.
 * @slot droppable-description - Droppable description text.
 */
@customElement("slh-file-input")
export default class SlhFileInput
  extends Translatable(SlhCoreFileInput(LitElement))
  implements SlhFormElement
{
  @queryAssignedNodes({ slot: "label" })
  protected labelSlotNodes: Array<HTMLElement> | undefined;

  @queryAssignedNodes({ slot: "droppable-description" })
  protected droppableDescriptionSlotNodes: Array<HTMLElement> | undefined;

  @query(".input-files-holder", false)
  private inputHolder!: HTMLInputElement;

  @query(".input-files-wrapper", false)
  private inputWrapper!: HTMLElement;

  @query(".file-input-container", false)
  private fileContainer!: HTMLElement;

  @query(".file-input-items-wrapper", false)
  private uploadedFilesHolder!: HTMLElement;

  static styles = style;

  /**
   * Icon Type for file upload input
   * @attr {string} icon-type
   * @type {string}
   */
  @property({
    type: String,
    attribute: "icon-type",
    reflect: true,
  })
  iconType?: IconType | null = null;

  /**
   * Renders file input in error state
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  error: boolean = false;

  /**
   * Renders drag & drop file input
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  droppable: boolean = false;

  /**
   * Renders progressible files
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  progressible: boolean = false;

  private actionLabel: string = "";

  @state()
  private hasDroppableDescription: boolean = false;

  onDrop: (this: HTMLElement, ev: DragEvent) => any = () => {};

  onHostFocus: (e: Event) => any = () => {};

  onHostBlur: (e: Event) => any = () => {};

  connectedCallback() {
    super.connectedCallback();
    this.onHostFocus = this.hostFocus.bind(this);
    this.onHostBlur = this.hostBlur.bind(this);
    this.addEventListener("focus", this.onHostFocus);
    this.addEventListener("blur", this.onHostBlur);
  }

  disconnectedCallback() {
    this.removeEventListener("focus", this.onHostFocus);
    this.removeEventListener("blur", this.onHostBlur);
    super.disconnectedCallback();
  }

  protected firstUpdated(): void {
    // @ts-ignore
    super.firstUpdated();
    this.labelSlotNodes?.forEach((node: HTMLElement) => {
      if (!node.nodeName.endsWith("-LABEL")) {
        node.remove();
      }
    });
    if (this.labelSlotNodes !== undefined && this.labelSlotNodes?.length > 0) {
      this.inputWrapper.classList.add("has-label");
    }

    this.showDroppableDescription();

    this.actionLabel = this.getLabel();

    if (this.droppable && !this.disabled) {
      this.onDrop = this.drop.bind(this);
      ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
        this.fileContainer?.addEventListener(eventName, this.preventDefaults);
      });
      ["dragenter", "dragover"].forEach((eventName) => {
        this.fileContainer?.addEventListener(
          eventName,
          this.highlight.bind(this)
        );
      });
      ["dragleave", "drop"].forEach((eventName) => {
        this.fileContainer?.addEventListener(
          eventName,
          this.unhighlight.bind(this)
        );
      });
      this.fileContainer?.addEventListener("drop", this.onDrop);
    }
  }

  getActionLabel(): string {
    if (this.actionLabel === "") {
      return this.trans("file-input.button-label");
    }
    return this.actionLabel;
  }

  /**
   * @returns {ClassInfo}
   */
  renderInputClasses(): ClassInfo {
    return {
      "show-block": this.multiple && this.files.length > 0,
      "show-inline": !this.multiple && this.files.length > 0,
    };
  }

  /**
   * @returns {ClassInfo}
   */
  renderClasses(): ClassInfo {
    return {
      "drag-drop": this.droppable,
    };
  }

  render(): TemplateResult {
    return html` ${this.droppable ? this.renderLabel() : nothing}
      <div
        class="file-input-container ${classMap(this.renderClasses())}"
        ?disabled="${this.disabled}"
        ?error="${this.error}"
      >
        <div class="input-files-wrapper ${classMap(this.renderInputClasses())}">
          ${this.renderUploadIcon()}
          ${!this.droppable
            ? this.renderLabel()
            : this.renderDroppableDescription()}
          <div
            class="input-files-holder"
            tabindex="0"
            @keydown="${this.handleKeydown}"
          >
            ${this.renderInputElement()}
          </div>
        </div>
        ${this.renderHint()} ${this.renderError()}
      </div>
      <div class="file-input-items-wrapper ${classMap(this.renderClasses())}">
        ${this.renderExistingFiles()}
      </div>`;
  }

  /**
   * @param {KeyboardEvent} event
   * w3.org keyboard support
   */
  protected handleKeydown(event: KeyboardEvent) {
    const { code } = event;
    if (code === "Enter" || code === "NumpadEnter" || code === "Space") {
      event.preventDefault();
      this.inputElement.click();
    }
  }

  /**
   * render list of chips
   * @returns {HTMLElement[]}
   */
  renderExistingFiles(): HTMLElement[] {
    const htmlFiles: HTMLElement[] = [];
    this.existingFiles?.forEach((uploadedFile: ExistingFile) => {
      htmlFiles.push(this.renderExistingFile(uploadedFile));
    });
    this.getUploadedFiles().forEach((uploadedFile: UploadedFile) => {
      htmlFiles.push(this.renderUploadedFiles(uploadedFile));
    });
    return htmlFiles;
  }

  /**
   * Returns slh-file-input-item
   */
  protected renderUploadedFiles(uploadedFile: UploadedFile): HTMLElement {
    const slhUploadedFile = document.createElement(
      "slh-file-input-item"
    ) as SlhFileInputItem;
    slhUploadedFile.file = uploadedFile.file;
    slhUploadedFile.percentage = uploadedFile.percentage;
    slhUploadedFile.error = uploadedFile.error;
    slhUploadedFile.errorText = uploadedFile.errorText;
    slhUploadedFile.url = uploadedFile.url;
    slhUploadedFile.completed = uploadedFile.completed;
    slhUploadedFile.innerHTML = uploadedFile.file.name;
    slhUploadedFile.progressible = this.progressible;
    slhUploadedFile.addEventListener(FILE_REMOVE, () => {
      this.removeFile(uploadedFile.file);
    });
    return slhUploadedFile;
  }

  /**
   * Returns slh-file-input-item
   */
  protected renderExistingFile(uploadedFile: ExistingFile): HTMLElement {
    const slhUploadedFile = document.createElement(
      "slh-file-input-item"
    ) as SlhFileInputItem;
    slhUploadedFile.file = uploadedFile;
    slhUploadedFile.innerHTML = uploadedFile.name;
    slhUploadedFile.progressible = this.progressible;
    slhUploadedFile.addEventListener(EXISTING_FILE_REMOVE, () => {
      this.removeExistingFile(uploadedFile);
    });
    return slhUploadedFile;
  }

  // eslint-disable-next-line class-methods-use-this
  renderLabel(): TemplateResult {
    return html`<slot name="label"></slot>`;
  }

  // eslint-disable-next-line class-methods-use-this
  renderDroppableDescription(): TemplateResult {
    return html`<div
      class="droppable-description-holder ${classMap(
        this.renderDroppableDescriptionClasses()
      )}"
    >
      <slot
        name="droppable-description"
        @slotchange="${this.showDroppableDescription}"
      ></slot>
    </div>`;
  }

  private showDroppableDescription() {
    if (
      this.droppableDescriptionSlotNodes !== undefined &&
      this.droppableDescriptionSlotNodes?.length > 0
    ) {
      this.hasDroppableDescription = true;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  renderError(): TemplateResult {
    return html`<div
      class="error-holder ${classMap(this.renderErrorClasses())}"
    >
      <div class="error-icon-holder">${this.renderErrorIcon()}</div>
      <div class="error-messages-holder">
        <slot name="error"></slot>
      </div>
    </div>`;
  }

  renderUploadIcon(): TemplateResult {
    if (this.droppable) {
      const icon: SlhIcon = new SlhIcon();
      icon.type = IconType.ICO_PLUS;
      icon.width = Constants.FILE_INPUT_ICON_WIDTH;
      icon.height = Constants.FILE_INPUT_ICON_HEIGHT;
      return html`<div class="drag-drop-upload-icon">${icon}</div>`;
    }

    return html``;
  }

  renderIcon(): TemplateResult {
    let icon: SlhIcon | null = null;
    if (this.iconType) {
      icon = new SlhIcon();
      icon.type = this.iconType;
      icon.width = Constants.FILE_INPUT_ICON_WIDTH;
      icon.height = Constants.FILE_INPUT_ICON_HEIGHT;
    }

    return html`${icon}`;
  }

  protected renderLoader(): TemplateResult {
    let loading: SlhLoader | null = null;
    let color;
    if (this.loading) {
      loading = new SlhLoader();
      setTimeout(() => {
        if (this.disabled) {
          color = Constants.FILE_INPUT_DISABLED_LOADER_COLOR;
        } else {
          color = Constants.FILE_INPUT_LOADER_COLOR;
        }
        loading?.shadowRoot?.querySelector("svg")?.classList.add(color);
      });
    }
    return html`${loading}`;
  }

  protected drop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const files: FileList | undefined = event.dataTransfer?.files;
    if (files) {
      this.addFiles(files);
    }
    this.dispatchChangeEvent();
  }

  // eslint-disable-next-line class-methods-use-this
  protected preventDefaults(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  protected highlight(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.fileContainer.classList.add(Constants.FILE_INPUT_HIGHLIGHTED_CLASS);
  }

  protected unhighlight(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.fileContainer.classList.remove(Constants.FILE_INPUT_HIGHLIGHTED_CLASS);
  }

  setUploadedFile(
    file: File,
    percentage: number = 0,
    completed: boolean = false,
    url: string = "",
    error: boolean = false,
    errorText: string = ""
  ) {
    const value: UploadedFile = {
      file,
      percentage,
      completed,
      url,
      error,
      errorText,
    };
    this.updateUploadedFiles(value);
    this.uploadedFilesHolder
      .querySelectorAll("slh-file-input-item")
      ?.forEach((element) => {
        const slhFileInputValue = element as SlhFileInputItem;
        if (value.file === slhFileInputValue.file) {
          slhFileInputValue.percentage = value.percentage;
          slhFileInputValue.completed = value.completed;
          slhFileInputValue.error = value.error;
          slhFileInputValue.errorText = value.errorText;
          slhFileInputValue.error = value.error;
        }
      });
  }

  // eslint-disable-next-line class-methods-use-this
  private getHintIcon(): HTMLElement {
    return SlhFormIcons.getHintIcon();
  }

  // eslint-disable-next-line class-methods-use-this
  private renderErrorIcon(): TemplateResult {
    return SlhFormIcons.renderErrorIcon();
  }

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  private renderErrorClasses(): ClassInfo {
    return {
      hidden: !this.error,
    };
  }

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  private renderDroppableDescriptionClasses(): ClassInfo {
    return {
      hidden: !this.hasDroppableDescription,
    };
  }

  private hostFocus(): void {
    this.inputHolder.focus();
    this.setAttribute("tabindex", "-1");
  }

  private hostBlur(): void {
    this.setAttribute("tabindex", "0");
  }
}
